// Do Not Disturb: this device's copy gates the CLIENT notify policy + sound; setMyDnd
// (app.svelte.ts) also sends it to the relay, whose copy (user_presence.dnd_until)
// silences push on every device.
//
// Persistence mirrors UserStatusState: a per-user localStorage key bound on
// login (bindUser) so a shared device doesn't bleed one user's prefs into
// another's.

const BASE = "cyborg7_notify_client_prefs";

export class NotifyClientPrefsState {
  // Do Not Disturb: suppress every client banner/sound except personal mentions.
  dnd = $state(false);

  private _boundUserId: string | null = null;

  // null while unbound — see FavoritesState.storageKey for why the old fallback
  // to the unsuffixed base key was residue worth removing.
  private get storageKey(): string | null {
    return this._boundUserId ? `${BASE}_${this._boundUserId}` : null;
  }

  bindUser(userId: string): void {
    this._boundUserId = userId;
    this.dnd = false;
    this.load();
  }

  clearLocal(): void {
    this._boundUserId = null;
    this.dnd = false;
  }

  setDnd(active: boolean): void {
    this.dnd = active;
    this.persist();
  }

  toggleDnd(): void {
    this.setDnd(!this.dnd);
  }

  // The first launch per user and device after CYBORG-1483: true when DND is on, so a DND set
  // before the relay persisted it reaches push. Marked done either way, so no later launch
  // re-sends and a stale device cannot switch DND back on for every device.
  takeRelaySync(): boolean {
    const key = this.storageKey;
    if (!key) return false;
    const stored = this.readStored(key);
    if (stored.dndRelaySynced === true) return false;
    try {
      localStorage.setItem(key, JSON.stringify({ ...stored, dndRelaySynced: true }));
    } catch {
      // intentional: private mode / quota — the one-time sync may repeat next launch.
    }
    return this.dnd;
  }

  private readStored(key: string): Record<string, unknown> {
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "{}");
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      // intentional: a corrupt or unreadable payload means "nothing stored".
      return {};
    }
  }

  // Merges into the stored blob so fields from the removed keyword / broadcast /
  // sound settings survive untouched (reversible), they are just no longer read.
  private persist(): void {
    const key = this.storageKey;
    if (!key) return; // unbound (signed out) ⇒ no owner to write under.
    try {
      localStorage.setItem(key, JSON.stringify({ ...this.readStored(key), dnd: this.dnd }));
    } catch {
      // intentional: private mode / quota — the pref stays in-memory for the session.
    }
  }

  private load(): void {
    const key = this.storageKey;
    if (!key) return;
    this.dnd = this.readStored(key).dnd === true;
  }
}

export const notifyClientPrefsState = new NotifyClientPrefsState();
